import { Box, Chip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Disparity } from '../../types/Disparity/Disparity.type';
import { NewDisparity } from '../../types/Disparity/NewDisparity.type';
import { ComparisonPoolType } from '../../types/enums/ComparisonPoolType.enum';
import { Statute } from '../../types/Statute.type';

type ComparisonPoolsPreviewProps = {
  disparity?: Disparity;
  pools?: NewDisparity['comparison_pools'];
  newDisparity?: boolean;
  statuteMap?: Map<number, Statute>;
};

export default function ComparisonPoolsPreview({
  disparity,
  pools,
  newDisparity,
  statuteMap,
}: ComparisonPoolsPreviewProps) {
  const comparisonPools = useMemo(() => {
    if (pools) {
      return {
        inclusion:
          pools.inclusion?.map((group) =>
            group.map((id) => ({
              id,
              name: statuteMap?.get(id)?.display_name ?? id.toString(),
            })),
          ) ?? [],
        exclusion:
          pools.exclusion?.map((group) =>
            group.map((id) => ({
              id,
              name: statuteMap?.get(id)?.display_name ?? id.toString(),
            })),
          ) ?? [],
      };
    }

    if (disparity) {
      const inclusion: { id: number; name: string }[][] = [];
      const exclusion: { id: number; name: string }[][] = [];

      disparity.comparison_pools.forEach((pool) => {
        const statutes = pool.comparison_pool_statutes.map((s) => ({
          id: s.statute_id,
          name: s.display_name || s.statute_id.toString(),
        }));

        if (pool.comparison_pool_type.id === ComparisonPoolType.INCLUSION) inclusion.push(statutes);
        if (pool.comparison_pool_type.id === ComparisonPoolType.EXCLUSION) exclusion.push(statutes);
      });

      return { inclusion, exclusion };
    }

    return { inclusion: [], exclusion: [] };
  }, [disparity, pools, statuteMap]);

  return (
    (comparisonPools.inclusion.length > 0 || comparisonPools.exclusion.length > 0) && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: newDisparity ? 3 : 5 }}>
        {/* Inclusion */}
        <Box sx={{ display: 'flex', flexDirection: newDisparity ? 'column' : 'row', gap: newDisparity ? 1 : 3 }}>
          {comparisonPools.inclusion.length > 0 && (
            <Typography variant='body2' sx={{ fontWeight: 500, minWidth: 120 }}>
              Comparator pool:
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
            {comparisonPools.inclusion.map((caseGroup, idx) => (
              <Box
                key={`inclusion-pool-${idx}`}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  backgroundColor: '#edf2f7',
                  borderRadius: '8px',
                  padding: '6px',
                }}
              >
                {caseGroup.map((statute) => (
                  <Chip
                    key={statute.id}
                    size='medium'
                    label={statute.name}
                    variant='filled'
                    sx={{
                      borderRadius: '6px',
                      backgroundColor: 'accentBlue.main',
                      color: 'white',
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Exclusion */}
        <Box sx={{ display: 'flex', flexDirection: newDisparity ? 'column' : 'row', gap: newDisparity ? 1 : 3 }}>
          {comparisonPools.exclusion.length > 0 && (
            <Typography variant='body2' sx={{ fontWeight: 500, minWidth: 120 }}>
              Exclusion pool:
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
            {comparisonPools.exclusion.map((caseGroup, idx) => (
              <Box
                key={`exclusion-pool-${idx}`}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  backgroundColor: '#edf2f7',
                  borderRadius: '8px',
                  padding: '6px',
                }}
              >
                {caseGroup.map((statute) => (
                  <Chip
                    key={statute.id}
                    size='medium'
                    label={statute.name}
                    variant='filled'
                    sx={{
                      borderRadius: '6px',
                      backgroundColor: 'accentBlue.main',
                      color: 'white',
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    )
  );
}
